import { Fragment, useState } from 'react';

import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import styled from 'styled-components';

import { Button } from '@components/button';
import { CollapseCard } from '@components/card/collapse-card';
import { createForm, useCreateFormStore } from '@components/form';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconApi } from '@components/icons/api';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconList } from '@components/icons/list';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOutput } from '@components/icons/output';
import { IconRemove } from '@components/icons/remove';
import { IconSend } from '@components/icons/send';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { MenuItem } from '@components/menu/menu';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import {
  ClampedText,
  DraggableIcon,
  FieldTable,
  MethodTag,
  generateTemplateName,
  getFormEndpointVariables,
  getVariableCount,
  methodFancyContainerColorMap,
} from './common';
import { endpointContext } from './endpoint-fields';
import {
  useDeleteIntegrationEndpoint,
  useListsScope,
  useUpdateIntegrationEndpoint,
  useUpdateIntegrationEndpointDefaultFiles,
  useUpdateIntegrationEndpointExampleFiles,
} from './integrations-hooks';
import type {
  IntegrationEndpoint,
  IntegrationEndpointVariable,
  IntegrationRequestHeader,
} from './integrations-types';
import { useDuplicateIntegrationEndpointModal } from './modals/duplicate-endpoint';
import { useTestRequestModal } from './modals/test-request';
import { ResponseValueCard } from './response-values';
import { useIntegrationEndpointCreateSidebar } from './sidebars/add-edit-endpoint';
import { Grid } from './styles';

export type EndpointCardProps = {
  endpoint: IntegrationEndpoint<string>;
  baseUrl?: string;
  dragAttributes?: DraggableAttributes;
  dragListeners?: SyntheticListenerMap;
};

export const EndpointCard = ({
  endpoint,
  baseUrl,
  dragAttributes,
  dragListeners,
}: EndpointCardProps) => {
  const { t } = useScopedTranslate(locale);
  const { templateId } = useListsScope();
  const [responseValuesCollapsed, setResponseValuesCollapsed] = useState(true);
  const integrationId = endpoint.integration;
  const showEditSidebar = useIntegrationEndpointCreateSidebar();
  const showDuplicateModal = useDuplicateIntegrationEndpointModal();
  const showTestRequestModal = useTestRequestModal();
  const showDeleteModal = useDeleteModal();
  const deleteMutation = useDeleteIntegrationEndpoint(endpoint.integration, endpoint.shortId);
  const url = (endpoint.url ?? '').trim();
  const isFullUrl = /^(https?:\/\/|\/\/|www\.)/i.test(url);
  const isPathWithBaseUrl = Boolean(baseUrl) && url.startsWith('/');
  const isUrlValid = isFullUrl || isPathWithBaseUrl;

  return (
    <CollapseCard
      defaultCollapsed
      space={4}
      variant="white"
      key={endpoint.shortId}
      renderCollapse={() => (
        <Stack space={2}>
          <RequestValuesCard
            endpoint={endpoint}
            onTestRequest={() => showTestRequestModal({ endpointId: endpoint.shortId, templateId })}
          />
          <RequestHeadersCard
            endpoint={endpoint}
            key={`req-headers-${JSON.stringify(endpoint.requestHeaders)}`}
          />
          <ResponseValueCard
            endpoint={endpoint}
            defaultCollapsed={responseValuesCollapsed}
            onVisibilityToggle={setResponseValuesCollapsed}
            key={`res-values-${(endpoint.responseValues ?? []).length}`}
          />
          <ResponseHeadersCard
            endpoint={endpoint}
            key={`res-headers-${JSON.stringify(endpoint.responseHeaders)}`}
          />
        </Stack>
      )}
    >
      <Stack>
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Stack space={4} orientation="horizontal" align="center">
            <DraggableIcon
              variant={methodFancyContainerColorMap[endpoint.method]}
              icon={<IconApi />}
              dragAttributes={dragAttributes}
              dragListeners={dragListeners}
            />
            <div>
              <ClampedText text={endpoint.name} font="semibold14" />
              {endpoint.description && (
                <ClampedText text={endpoint.description} font="regular14" color="textSecondary" />
              )}
              <Stack orientation="horizontal" align="center">
                <ClampedText text={endpoint.url} font="regular12" color="textHelper" />
                {!isUrlValid && <IconFormIndicatorWarning size={16} tooltip={t('invalidUrl')} />}
              </Stack>
            </div>
          </Stack>
          <Stack space={4} orientation="horizontal" align="center">
            <MethodTag method={endpoint.method} />
            <Menu buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}>
              <MenuItem
                icon={<IconEdit />}
                label={t('core.edit')}
                onSelect={() => showEditSidebar({ integrationId, endpoint })}
              />
              <MenuItem
                icon={<IconDuplicate />}
                label={t('core.duplicate')}
                onSelect={() => showDuplicateModal({ endpoint })}
              />
              <Menu.Separator />
              <MenuItem
                icon={<IconRemove />}
                label={t('deleteEndpoint')}
                variant="danger"
                onSelect={() =>
                  showDeleteModal({
                    title: t('deleteEndpoint'),
                    description: t('areYouSureDelete'),
                    buttonText: t('deleteEndpoint'),
                    variant: 'LARGE',
                    hideCannotBeUndone: true,
                    onConfirm: () => deleteMutation.mutate(),
                  })
                }
              />
            </Menu>
          </Stack>
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

type RequestValuesCardProps = {
  endpoint: IntegrationEndpoint<string>;
  onTestRequest?: () => void;
};

type Data = {};

const Form = createForm<Data>();

const getDefaultValueFiles = (
  variables: IntegrationEndpointVariable[],
  allVariables: IntegrationEndpointVariable[],
  formData: FormData
) => {
  variables.forEach((variable) => {
    if (variable.type === 'file') {
      const variableName = generateTemplateName(allVariables, variable);
      const value = variable.default_value;
      if (value instanceof File) {
        formData.append(variableName, value as File);
      }
      if (value === null) {
        formData.append(`${variableName}__remove`, '');
      }
      variable.default_value = '';
    }
    if (variable.type === 'array' && variable.template) {
      getDefaultValueFiles(variable.template, allVariables, formData);
    }
  });
};

export const getExampleValueFiles = (
  variables: IntegrationEndpointVariable[],
  allVariables: IntegrationEndpointVariable[],
  formData: FormData
) => {
  variables.forEach((variable) => {
    if (variable.type === 'file') {
      const variableName = generateTemplateName(allVariables, variable);
      const value = variable.example;
      if (value instanceof File) {
        formData.append(variableName, value as File);
      }
      if (value === null) {
        formData.append(`${variableName}__remove`, '');
      }
      variable.example = '';
    }
    if (variable.type === 'array' && variable.template) {
      getExampleValueFiles(variable.template, allVariables, formData);
    }
  });
};

export const RequestValuesCard = ({ endpoint, onTestRequest }: RequestValuesCardProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const updateMutation = useUpdateIntegrationEndpoint();
  const defaultFilesMutation = useUpdateIntegrationEndpointDefaultFiles();
  const exampleFileMutation = useUpdateIntegrationEndpointExampleFiles();

  const endpointVariables = endpoint.variables as IntegrationEndpointVariable[];
  const visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: false,
    default: true,
    visible: true,
    format: false,
  };

  const handleUpdateEndpoint = async () => {
    const variables = getFormEndpointVariables({
      formStore,
      endpointVariables,
      visibleColumns,
    });
    const defaultFiles = new FormData();
    getDefaultValueFiles(variables, variables, defaultFiles);
    const exampleFiles = new FormData();
    getExampleValueFiles(variables, variables, exampleFiles);

    await updateMutation.mutateAsync({
      shortId: endpoint.shortId,
      data: {
        variables,
      },
    });

    if (visibleColumns.example && Array.from(exampleFiles.keys()).length > 0) {
      exampleFileMutation.mutateAsync({
        shortId: endpoint.shortId,
        data: exampleFiles,
      });
    }
    if (visibleColumns.default && Array.from(defaultFiles.keys()).length > 0) {
      defaultFilesMutation.mutateAsync({
        shortId: endpoint.shortId,
        data: defaultFiles,
      });
    }
  };
  const formActionPortalId = `${endpoint.shortId}-form-actions-portal`;

  return (
    <endpointContext.Provider value={endpoint}>
      <CollapseCard
        keepCollapsedInDom
        defaultCollapsed
        spaceX={4}
        spaceY={2}
        variant="gray"
        renderCollapse={() => (
          <Form store={formStore} onSubmit={handleUpdateEndpoint}>
            <FieldTable
              form={Form}
              endpointId={endpoint.shortId}
              endpointVariables={endpointVariables}
              visibleColumns={visibleColumns}
              requestType={endpoint.requestType}
            />
            {isDirty && (
              <Form.Actions portal={formActionPortalId}>
                <Stack orientation="horizontal" space={2} align="center">
                  <Form.Reset stopPropagation text={t('core.cancel')} variant="secondary" />
                  <Form.Submit
                    stopPropagation
                    isDisabled={updateMutation.isLoading}
                    text={t('core.save')}
                    variant="primary-blue"
                  />
                </Stack>
              </Form.Actions>
            )}
          </Form>
        )}
      >
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack orientation="horizontal" align="center" space={2}>
            <IconOutput color="elementPurple50" />
            <Text text={t('requestValues')} font="semibold14" />
            <Text text="•" color="textHelper" />
            <Text text={getVariableCount(endpointVariables)} font="semibold14" />
          </Stack>
          <Stack orientation="horizontal" space={4} align="center">
            <Stack space={2} orientation="horizontal" align="center">
              {!isDirty && onTestRequest && (
                <Button
                  stopPropagation
                  text={t('test')}
                  iconLeft={<IconSend />}
                  variant="ghost"
                  onClick={onTestRequest}
                />
              )}
              <div id={formActionPortalId} />
            </Stack>
          </Stack>
        </Stack>
      </CollapseCard>
    </endpointContext.Provider>
  );
};

const createEmptyRequestHeader = (): IntegrationRequestHeader => ({
  name: '',
  label: '',
  default_value: '',
  visible: true,
});

type RequestHeaderRowProps = {
  header: IntegrationRequestHeader;
  onChange: (field: keyof IntegrationRequestHeader, value: string | boolean) => void;
  onRemove: () => void;
};

const RequestHeaderRow = ({ header, onChange, onRemove }: RequestHeaderRowProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Fragment>
      <HeaderCell>
        <TextField
          label={t('headerName')}
          hideLabel
          value={header.name}
          onChange={(value) => onChange('name', value)}
          placeholder={t('requestHeaderNamePlaceholder')}
        />
      </HeaderCell>
      <HeaderCell>
        <TextField
          label={t('headerLabel')}
          hideLabel
          value={header.label}
          onChange={(value) => onChange('label', value)}
          placeholder={t('labelPlaceholder')}
        />
      </HeaderCell>
      <HeaderCell>
        <TextField
          label={t('headerDefaultValue')}
          hideLabel
          value={header.default_value}
          onChange={(value) => onChange('default_value', value)}
          placeholder={t('defaultValuePlaceholder')}
        />
      </HeaderCell>
      <HeaderCell>
        <Switch
          label={t('headerVisible')}
          labelVisible={false}
          value={header.visible}
          onChange={(value) => onChange('visible', value)}
        />
      </HeaderCell>
      <HeaderCell>
        <IconButton icon={<IconTrash />} onClick={onRemove} variant="ghost-purple" />
      </HeaderCell>
    </Fragment>
  );
};

type RequestHeadersCardProps = {
  endpoint: IntegrationEndpoint<string>;
};

export const RequestHeadersCard = ({ endpoint }: RequestHeadersCardProps) => {
  const { t } = useScopedTranslate(locale);
  const updateMutation = useUpdateIntegrationEndpoint();

  const serverHeaders = endpoint.requestHeaders ?? [];
  const [headers, setHeaders] = useState<IntegrationRequestHeader[]>(serverHeaders);

  const isDirty = JSON.stringify(headers) !== JSON.stringify(serverHeaders);

  const handleAdd = () => {
    setHeaders((prev) => [...prev, createEmptyRequestHeader()]);
  };

  const handleRemove = (index: number) => {
    setHeaders((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (
    index: number,
    field: keyof IntegrationRequestHeader,
    value: string | boolean
  ) => {
    setHeaders((prev) => {
      if (prev[index]?.[field] === value) return prev;
      return prev.map((header, i) => (i === index ? { ...header, [field]: value } : header));
    });
  };

  const handleSave = async () => {
    await updateMutation.mutateAsync({
      shortId: endpoint.shortId,
      data: { requestHeaders: headers },
    });
  };

  const handleCancel = () => {
    setHeaders(serverHeaders);
  };

  return (
    <CollapseCard
      defaultCollapsed
      spaceX={4}
      spaceY={2}
      variant="gray"
      renderCollapse={() => (
        <Stack space={2}>
          {headers.length > 0 && (
            <Grid $numColumns={3} $numSmallColumns={2}>
              <HeaderCellLabel>
                <Text text={t('headerName')} font="semibold14" color="textSecondary" />
              </HeaderCellLabel>
              <HeaderCellLabel>
                <Text text={t('headerLabel')} font="semibold14" color="textSecondary" />
              </HeaderCellLabel>
              <HeaderCellLabel>
                <Text text={t('headerDefaultValue')} font="semibold14" color="textSecondary" />
              </HeaderCellLabel>
              <HeaderCellLabel>
                <Text text={t('headerVisible')} font="semibold14" color="textSecondary" />
              </HeaderCellLabel>
              <HeaderCellLabel />
              {headers.map((header, index) => (
                <RequestHeaderRow
                  key={index}
                  header={header}
                  onChange={(field, value) => handleChange(index, field, value)}
                  onRemove={() => handleRemove(index)}
                />
              ))}
            </Grid>
          )}
          <div>
            <Button
              stopPropagation
              text={t('addHeader')}
              variant="secondary"
              iconLeft={<IconAddPlus />}
              onClick={handleAdd}
            />
          </div>
        </Stack>
      )}
    >
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack orientation="horizontal" align="center" space={2}>
          <IconList color="elementPurple50" />
          <Text text={t('requestHeaders')} font="semibold14" />
          <Text text="•" color="textHelper" />
          <Text text={headers.length} font="semibold14" />
        </Stack>
        <Stack orientation="horizontal" space={4} align="center">
          <Stack space={2} orientation="horizontal" align="center">
            {isDirty && (
              <>
                <Button
                  stopPropagation
                  text={t('core.cancel')}
                  variant="secondary"
                  onClick={handleCancel}
                />
                <Button
                  stopPropagation
                  text={t('core.save')}
                  variant="primary-blue"
                  isDisabled={updateMutation.isLoading}
                  onClick={handleSave}
                />
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

type ResponseHeadersCardProps = {
  endpoint: IntegrationEndpoint<string>;
};

export const ResponseHeadersCard = ({ endpoint }: ResponseHeadersCardProps) => {
  const { t } = useScopedTranslate(locale);
  const updateMutation = useUpdateIntegrationEndpoint();
  const headers = endpoint.responseHeaders ?? [];

  const handleRemove = async (index: number) => {
    const updated = headers.filter((_, i) => i !== index);
    await updateMutation.mutateAsync({
      shortId: endpoint.shortId,
      data: { responseHeaders: updated },
    });
  };

  return (
    <CollapseCard
      defaultCollapsed
      spaceX={4}
      spaceY={2}
      variant="gray"
      renderCollapse={() => (
        <Stack space={2}>
          {headers.length > 0 ? (
            <Grid $numColumns={1} $numSmallColumns={1}>
              <HeaderCellLabel>
                <Text text={t('httpHeader')} font="semibold14" color="textSecondary" />
              </HeaderCellLabel>
              <HeaderCellLabel />
              {headers.map((header, index) => (
                <Fragment key={header.name}>
                  <HeaderCell>
                    <Text text={header.header} font="regular14" />
                  </HeaderCell>
                  <HeaderCell>
                    <IconButton
                      icon={<IconTrash />}
                      onClick={() => handleRemove(index)}
                      variant="ghost-purple"
                    />
                  </HeaderCell>
                </Fragment>
              ))}
            </Grid>
          ) : (
            <Text text={t('noResponseHeaders')} font="regular14" color="textHelper" />
          )}
        </Stack>
      )}
    >
      <Stack orientation="horizontal" align="center" space={2}>
        <IconList color="elementPurple50" />
        <Text text={t('responseHeaders')} font="semibold14" />
        <Text text="•" color="textHelper" />
        <Text text={headers.length} font="semibold14" />
      </Stack>
    </CollapseCard>
  );
};

const HeaderCell = styled.div({
  padding: pxToRem(8),
  borderBottom: `1px solid ${theme.color.uiTrans10}`,
  display: 'flex',
  alignItems: 'center',
  minHeight: pxToRem(48),
});

const HeaderCellLabel = styled(HeaderCell)({
  paddingBottom: pxToRem(4),
});

const locale = {
  en: {
    deleteEndpoint: 'Delete Endpoint',
    areYouSureDelete:
      'You are about to permanently delete this endpoint. The following will be affected:\n• All current configurations and response values\n• Journeys that rely on this endpoint\n• Travellers who are currently in progress\n\nOnce deleted, you can recreate this endpoint, but any lost data or configurations cannot be restored.',
    requestValues: 'Request values',
    test: 'Test',
    invalidUrl:
      'Invalid URL. Make sure the full URL is provided (either in the base URL field or as a complete address) and try again.',
    requestHeaders: 'Request headers',
    responseHeaders: 'Response headers',
    headerName: 'Header name',
    headerLabel: 'Label',
    headerDefaultValue: 'Default value',
    headerVisible: 'Visible',
    variableName: 'Variable name',
    httpHeader: 'HTTP header',
    addHeader: 'Add header',
    noResponseHeaders:
      'No response headers captured yet. Run a test request to auto-detect headers.',
    requestHeaderNamePlaceholder: 'e.g. If-Match',
    labelPlaceholder: 'Label',
    defaultValuePlaceholder: 'Default value',
  },
};
